import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";

const SidebarContext = createContext();

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    // Return a no-op implementation if context is not available
    // This allows components to work even if not wrapped in provider
    return {
      sidebarOpen: true,
      toggleSidebar: () => {},
      setSidebarState: () => {},
      collapseSidebar: () => {},
      restoreSidebar: () => {},
    };
  }
  return context;
};

export const SidebarProvider = ({ children }) => {
  const previousSidebarStateRef = useRef(true);
  // Tracks what the user last requested via the menu button.
  // Used to restore sidebar state after modal-driven collapses on mobile.
  const userSidebarStateRef = useRef(true);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // On small screens, keep the sidebar collapsed initially.
    // (<= 768px) should start closed; clicking the menu button will open it.
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    const initial = !isMobile;
    previousSidebarStateRef.current = initial;
    userSidebarStateRef.current = initial;
    return initial;
  });
  const isCollapsedByModalRef = useRef(false);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => {
      const newState = !prev;
      userSidebarStateRef.current = newState;
      if (!newState) {
        // If collapsing manually, clear modal flag
        isCollapsedByModalRef.current = false;
      }
      return newState;
    });
  }, []);

  const setSidebarState = useCallback((open) => {
    setSidebarOpen(open);
    userSidebarStateRef.current = open;
    if (!open) {
      isCollapsedByModalRef.current = false;
    }
  }, []);

  // Collapse sidebar and save previous state
  const collapseSidebar = useCallback(() => {
    setSidebarOpen((prev) => {
      if (prev && !isCollapsedByModalRef.current) {
        previousSidebarStateRef.current = prev;
        isCollapsedByModalRef.current = true;
        return false;
      }
      return prev;
    });
  }, []);

  // Restore sidebar to previous state
  const restoreSidebar = useCallback(() => {
    if (isCollapsedByModalRef.current) {
      const isMobile =
        typeof window !== "undefined" && window.innerWidth <= 768;

      // On mobile, restore to the user's last menu-toggle choice.
      // This prevents modal cleanups from auto-expanding the sidebar when
      // the initial state should be collapsed.
      setSidebarOpen(
        isMobile ? userSidebarStateRef.current : previousSidebarStateRef.current
      );
      isCollapsedByModalRef.current = false;
    }
  }, []);

  const value = {
    sidebarOpen,
    toggleSidebar,
    setSidebarState,
    collapseSidebar,
    restoreSidebar,
  };

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};
